import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const Settings = ({ user, onUserUpdate }) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Profile settings state
  const [profileData, setProfileData] = useState({
    name: "",
    username: "",
    email: "",
    profilePhoto: null,
    profilePhotoUrl: "",
  });

  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: "friends", // 'public', 'friends', 'private'
    allowTagging: "friends", // 'everyone', 'friends', 'none'
    showOnlineStatus: true,
    allowFriendRequests: true,
  });

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    tagNotifications: true,
    friendRequestNotifications: true,
    albumShareNotifications: true,
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || "",
        username: user.username || "",
        email: user.email || "",
        profilePhoto: null,
        profilePhotoUrl: user.profilePhotoUrl || "",
      });
    }
    // Load additional settings from API if needed
    fetchUserSettings();
  }, [user]);

  const fetchUserSettings = async () => {
    try {
      // Fetch privacy and notification settings if they exist in your backend
      // For now, using default values as the current schema doesn't include these
    } catch (error) {
      console.error("Error fetching user settings:", error);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size and type
      if (file.size > 5 * 1024 * 1024) {
        setError("Profile photo must be less than 5MB");
        return;
      }

      if (!file.type.match(/image\/(jpeg|jpg|png|gif)/i)) {
        setError("Only image files (JPG, PNG, GIF) are allowed");
        return;
      }

      setProfileData((prev) => ({
        ...prev,
        profilePhoto: file,
        profilePhotoUrl: URL.createObjectURL(file),
      }));
      setError(null);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append("name", profileData.name);
      formData.append("username", profileData.username);
      formData.append("email", profileData.email);

      if (profileData.profilePhoto) {
        formData.append("profilePhoto", profileData.profilePhoto);
        // Add a flag to indicate where to store the photo
        formData.append("photoType", "profilePicture");
      }

      const response = await axios.patch(
        `${process.env.REACT_APP_DATABASE_URL}/api/users/me`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Update the profileData with the permanent URL from the server
      setProfileData((prev) => ({
        ...prev,
        profilePhoto: null, // Clear file object after successful upload
        profilePhotoUrl: response.data.profilePhotoUrl || prev.profilePhotoUrl,
      }));

      setSuccess("Profile updated successfully!");
      onUserUpdate(response.data);

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Error updating profile:", error);
      setError(error.response?.data?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await axios.patch(
        `${process.env.REACT_APP_DATABASE_URL}/api/users/password`,
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }
      );

      setSuccess("Password updated successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Error updating password:", error);
      setError(error.response?.data?.message || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "DELETE MY ACCOUNT") {
      setError('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await axios.delete(`${process.env.REACT_APP_DATABASE_URL}/api/users/me`);

      // Logout and redirect
      localStorage.removeItem("authToken");
      delete axios.defaults.headers.common["Authorization"];
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      setError(error.response?.data?.message || "Failed to delete account");
      setSaving(false);
    }
  };

  const tabs = [
    { id: "profile", name: "Profile", icon: "user" },
    // { id: 'privacy', name: 'Privacy', icon: 'shield' },
    // { id: 'notifications', name: 'Notifications', icon: 'bell' },
    { id: "security", name: "Security", icon: "lock" },
    { id: "danger", name: "Account", icon: "exclamation" },
  ];

  const getIcon = (iconName) => {
    const icons = {
      user: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      ),
      shield: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
        />
      ),
      bell: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      ),
      lock: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      ),
      exclamation: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      ),
    };
    return icons[iconName];
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Settings
        </h1>
        <p className="mt-2 text-gray-600">
          Manage your account preferences and privacy settings
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 inline-flex items-center gap-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {getIcon(tab.icon)}
                </svg>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Profile Information
            </h3>

            <form onSubmit={handleProfileSubmit} className="space-y-6">
              {/* Profile Photo */}
              <div className="flex items-center space-x-6">
                <div className="flex-shrink-0">
                  <div className="h-24 w-24 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden">
                    {profileData.profilePhotoUrl ? (
                      <img
                        src={profileData.profilePhotoUrl}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl font-medium text-purple-700">
                        {profileData.name?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    Change Photo
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    JPG, PNG or GIF. Max size 5MB.
                  </p>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={profileData.name}
                  onChange={handleProfileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={profileData.username}
                  onChange={handleProfileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  This is how other users will find you
                </p>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Privacy Tab */}
        {activeTab === "privacy" && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Privacy Settings
            </h3>

            <div className="space-y-6">
              {/* Profile Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Profile Visibility
                </label>
                <div className="space-y-3">
                  {[
                    {
                      value: "public",
                      label: "Public",
                      desc: "Anyone can see your profile",
                    },
                    {
                      value: "friends",
                      label: "Friends Only",
                      desc: "Only your friends can see your profile",
                    },
                    {
                      value: "private",
                      label: "Private",
                      desc: "Only you can see your profile",
                    },
                  ].map((option) => (
                    <label key={option.value} className="flex items-start">
                      <input
                        type="radio"
                        name="profileVisibility"
                        value={option.value}
                        checked={
                          privacySettings.profileVisibility === option.value
                        }
                        onChange={(e) =>
                          setPrivacySettings((prev) => ({
                            ...prev,
                            profileVisibility: e.target.value,
                          }))
                        }
                        className="mt-0.5 h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                      />
                      <div className="ml-3">
                        <span className="text-sm font-medium text-gray-900">
                          {option.label}
                        </span>
                        <p className="text-sm text-gray-500">{option.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Tagging Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Who can tag you in photos?
                </label>
                <div className="space-y-3">
                  {[
                    {
                      value: "everyone",
                      label: "Everyone",
                      desc: "Anyone can tag you",
                    },
                    {
                      value: "friends",
                      label: "Friends Only",
                      desc: "Only your friends can tag you",
                    },
                    {
                      value: "none",
                      label: "No One",
                      desc: "No one can tag you",
                    },
                  ].map((option) => (
                    <label key={option.value} className="flex items-start">
                      <input
                        type="radio"
                        name="allowTagging"
                        value={option.value}
                        checked={privacySettings.allowTagging === option.value}
                        onChange={(e) =>
                          setPrivacySettings((prev) => ({
                            ...prev,
                            allowTagging: e.target.value,
                          }))
                        }
                        className="mt-0.5 h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                      />
                      <div className="ml-3">
                        <span className="text-sm font-medium text-gray-900">
                          {option.label}
                        </span>
                        <p className="text-sm text-gray-500">{option.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Other Privacy Options */}
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={privacySettings.showOnlineStatus}
                    onChange={(e) =>
                      setPrivacySettings((prev) => ({
                        ...prev,
                        showOnlineStatus: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">
                      Show online status
                    </span>
                    <p className="text-sm text-gray-500">
                      Let others see when you're active
                    </p>
                  </div>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={privacySettings.allowFriendRequests}
                    onChange={(e) =>
                      setPrivacySettings((prev) => ({
                        ...prev,
                        allowFriendRequests: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">
                      Allow friend requests
                    </span>
                    <p className="text-sm text-gray-500">
                      Let others send you friend requests
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => {
                    // Save privacy settings
                    setSuccess("Privacy settings saved!");
                    setTimeout(() => setSuccess(null), 3000);
                  }}
                  disabled={saving}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Notification Preferences
            </h3>

            <div className="space-y-6">
              <div className="space-y-4">
                {[
                  {
                    key: "emailNotifications",
                    label: "Email Notifications",
                    desc: "Receive notifications via email",
                  },
                  {
                    key: "pushNotifications",
                    label: "Push Notifications",
                    desc: "Receive push notifications in your browser",
                  },
                  {
                    key: "tagNotifications",
                    label: "Photo Tags",
                    desc: "Get notified when you're tagged in photos",
                  },
                  {
                    key: "friendRequestNotifications",
                    label: "Friend Requests",
                    desc: "Get notified about new friend requests",
                  },
                  {
                    key: "albumShareNotifications",
                    label: "Album Shares",
                    desc: "Get notified when someone shares an album with you",
                  },
                ].map((setting) => (
                  <label
                    key={setting.key}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {setting.label}
                      </span>
                      <p className="text-sm text-gray-500">{setting.desc}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={notificationSettings[setting.key]}
                      onChange={(e) =>
                        setNotificationSettings((prev) => ({
                          ...prev,
                          [setting.key]: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                  </label>
                ))}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => {
                    // Save notification settings
                    setSuccess("Notification settings saved!");
                    setTimeout(() => setSuccess(null), 3000);
                  }}
                  disabled={saving}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === "security" && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Security Settings
            </h3>

            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData((prev) => ({
                      ...prev,
                      currentPassword: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData((prev) => ({
                      ...prev,
                      newPassword: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Must be at least 8 characters long
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData((prev) => ({
                      ...prev,
                      confirmPassword: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {saving ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Danger Zone Tab */}
        {activeTab === "danger" && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Account Management
            </h3>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-red-900 mb-4">
                Danger Zone
              </h4>

              <div className="space-y-4">
                <div>
                  <h5 className="font-medium text-red-900">Delete Account</h5>
                  <p className="text-sm text-red-700 mb-4">
                    This action cannot be undone. This will permanently delete
                    your account, all your photos, and remove you from all
                    shared albums.
                  </p>

                  <button
                    onClick={() => setShowDeleteDialog(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Delete My Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success/Error Messages */}
        {success && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-green-600 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-green-800">{success}</span>
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-red-600 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-red-800">{error}</span>
          </div>
        )}
      </div>

      {/* Delete Account Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Account
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              This action is permanent and cannot be undone. All your photos,
              albums, and connections will be permanently deleted.
            </p>

            <p className="text-sm font-medium text-gray-900 mb-2">
              Type "DELETE MY ACCOUNT" to confirm:
            </p>

            <input
              type="text"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4"
              placeholder="DELETE MY ACCOUNT"
            />

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteConfirmation("");
                  setError(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={saving || deleteConfirmation !== "DELETE MY ACCOUNT"}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
